import { useState } from "react";
import { useActiveGroups, useWorkshopAdvisorGroups } from "@/lib/api";
import { AN_SUB } from "@/components/msd/analytics";
import { Icon } from "@/components/msd/Icon";
import { Sheet } from "@/components/msd/ui";
import { resolveJourney } from "@/lib/journey";
import { useTenantTerms } from "@/lib/TenantConfigContext";
import { createdRange, joinSummary, joinText, stepProgress } from "../../lib/advisorPerformance";

// One advisor, opened from the leaderboard: every group of theirs with whether the customer and the advisor are in the
// WhatsApp group (and for how long) and how far the group is through the service
// journey. All of it follows the date range and locations the page is showing.

const SECTION = "text-[15px] font-semibold tracking-[-0.01em]";

const JOIN_COLOR = { joined: "#17B26A", not_joined: "#F04438", no_number: "#98A2B3" };

function Who({ label, side }) {
  return (
    <span className="flex items-center gap-[6px]">
      <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: JOIN_COLOR[side.status] }} />
      <span className="text-msd-muted">{label}</span>{" "}
      <span className="font-medium text-msd-ink">{joinText(side)}</span>
    </span>
  );
}

const PAGE_SIZE = 10;

const FILTERS = [
  ["all", "All", () => true],
  ["customer", "Customer not joined", (g) => g.customer?.status === "not_joined"],
  ["advisor", "SA not joined", (g) => g.advisor?.status === "not_joined"],
];

function Groups({ query, rowsById, stageOverlay, featureFlags, onOpenGroup }) {
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  if (query.isError) return <div className={AN_SUB}>Couldn&apos;t load the groups.</div>;
  if (!query.data) return <div className={AN_SUB}>Loading…</div>;
  const all = query.data.groups;
  if (all.length === 0) return <div className={AN_SUB}>No service groups for this advisor in this period.</div>;
  const s = joinSummary(all);
  const test = FILTERS.find((f) => f[0] === filter)[2];
  const groups = all.filter(test);
  const pages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const shown = groups.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
  const pick = (key) => {
    setFilter(key);
    setPage(0);
  };
  const BTN =
    "cursor-pointer rounded-full border border-solid border-msd-line bg-transparent px-3 py-[5px] font-[inherit] text-[13px] hover:bg-msd-soft disabled:cursor-default disabled:opacity-40";
  return (
    <>
      <div className={AN_SUB} data-testid="msd-adv-groups-summary">
        {s.cars} {s.cars === 1 ? "group" : "groups"} · customer not in {s.customers} · SA not in {s.advisors}
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter groups">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => pick(key)}
            aria-pressed={filter === key}
            data-testid={`msd-adv-filter-${key}`}
            className={`${BTN} ${filter === key ? "!border-msd-ink font-semibold text-msd-ink" : "text-msd-muted"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {groups.length === 0 && <div className={AN_SUB}>No groups match this filter.</div>}
      <div className="flex flex-col divide-x-0 divide-y divide-solid divide-msd-line rounded-[12px] border border-solid border-msd-line">
        {shown.map((g) => {
          const row = rowsById.get(g.group_row_id);
          const progress = row ? stepProgress(resolveJourney(row, stageOverlay, featureFlags)) : null;
          return (
            <button
              key={g.group_row_id}
              type="button"
              onClick={() => onOpenGroup(g)}
              data-testid="msd-adv-group"
              className="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-3 text-left font-[inherit] hover:bg-msd-soft focus-visible:bg-msd-soft"
            >
              <span className="flex min-w-0 grow flex-col gap-[3px] text-[13.5px]">
                <b className="truncate text-[14.5px] font-semibold text-msd-ink">
                  {g.plate || "Group"}
                  {g.customer_name ? <span className="font-normal text-msd-muted"> · {g.customer_name}</span> : null}
                </b>
                <Who label="Customer" side={g.customer} />
                <Who label="SA" side={g.advisor} />
                <span className="text-msd-muted" data-testid="msd-adv-steps">
                  {progress
                    ? `${progress.done} of ${progress.total} steps${progress.latest ? ` · ${progress.latest}` : ""}`
                    : "Steps –"}
                </span>
              </span>
              <Icon name="right" size={14} stroke={2.2} color="var(--faint)" />
            </button>
          );
        })}
      </div>
      {groups.length > PAGE_SIZE && (
        <div className="flex items-center justify-between" data-testid="msd-adv-pager">
          <span className={AN_SUB}>
            {current * PAGE_SIZE + 1}–{Math.min((current + 1) * PAGE_SIZE, groups.length)} of {groups.length}
          </span>
          <div className="flex gap-2">
            <button type="button" className={BTN} disabled={current === 0} onClick={() => setPage(current - 1)} data-testid="msd-adv-prev">
              Previous
            </button>
            <button type="button" className={BTN} disabled={current >= pages - 1} onClick={() => setPage(current + 1)} data-testid="msd-adv-next">
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdvisorDetail({ advisor, base, workshopLabel = (x) => x, onClose, onOpenGroup }) {
  const groups = useWorkshopAdvisorGroups(base && { ...base, advisor_key: advisor.key });
  const desk = useActiveGroups();
  const { stageOverlay, featureFlags } = useTenantTerms();
  // First and last day a group of theirs was created, next to the heading.
  const range = createdRange(groups.data?.groups);
  const rowsById = new Map((desk.data?.rows || []).map((r) => [r._row, r]));
  return (
    <Sheet open title={advisor.name} onClose={onClose} testid="msd-advisor-detail" width={640}>
      {advisor.workshop && <div className={`${AN_SUB} -mt-3`}>{workshopLabel(advisor.workshop)}</div>}
      <section className="flex flex-col gap-3">
        <div className={SECTION}>
          All groups
          {range ? <span className="font-normal text-msd-muted" data-testid="msd-adv-created-range"> ({range})</span> : null}
        </div>
        <Groups query={groups} rowsById={rowsById} stageOverlay={stageOverlay} featureFlags={featureFlags} onOpenGroup={onOpenGroup} />
      </section>
    </Sheet>
  );
}
